import React, { useEffect, useRef, useState } from "react";
import { useGlobalState } from "./GlobalStateProvider";
import { fadeOutBGM, playBGM, playSE, stopBGM, toggleMute } from "./SoundEffect";
import DOMPurify from 'dompurify';
import "./Story.css";
import NazoPhase from "./NazoPhase";
import LogModal from "./LogModal";

const ORIGIN = "https://bad-assort.com/play/gothic-neue/origin.php";

type Data = {
  text?: string;
  image?: {
    src?: string;
    smooth?: boolean;
  };
  sound?: {
    type?: string,
    src?: string;
    stop?: boolean;
  }
  func?: string;
  next?: string;
  terminal?: string;
}[];

function Message({ currentText }: { currentText: string }) {
  const sanitizedText = DOMPurify.sanitize(currentText);
  return <div id="story-paragraph" key={currentText[0]} dangerouslySetInnerHTML={{ __html: sanitizedText }}></div>;
}

const Story: React.FC = () => {
  const { currentStep, setCurrentStep, logElements, setLogElements } = useGlobalState();

  const [isStoryVisible, setStoryVisible] = useState<boolean>(true);
  const [isNazoVisible, setNazoVisible] = useState<boolean>(false);
  const [isLogButtonVisible, setLogButtonVisible] = useState<boolean>(false);
  const [isLogVisible, setLogVisible] = useState<boolean>(false);
  const [pass, setPass] = useState<string>("");
  const [data, setData] = useState<Data>([]);
  const [index, setIndex] = useState<number>(-1);
  const [currentText, setCurrentText] = useState<string>("");
  const [currentImage, setCurrentImage] = useState<string>("");
  const [BGClass, setBGClass] = useState<string>("");
  const [isMute, setMute] = useState<boolean>(false);

  const turbulenceRef = useRef<SVGFETurbulenceElement>(null);
  const displacementRef = useRef<SVGFEDisplacementMapElement>(null);
  const svgFilterModeRef = useRef<string | null>(null);
  const startAnimateRef = useRef<number>(0);
  const ToggleLogRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (pass) {
      fetch(ORIGIN, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pass: pass }),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error("Network response was not ok");
          }
          return response.json();
        })
        .then((newData) => {
          console.log(newData);
          setData(() => newData);
          setIndex(() => 0);
          setStoryVisible(true);
          setCurrentImage("");
        }).catch((error) => {
          console.error("Fetch error:", error);
        });
    } else {
      setStoryVisible(false);
      console.log("no pass");
    }
  }, [pass]);

  const handleClick = () => {
    const newIndex: number = index + 1 < data.length ? index + 1 : index
    if (data[newIndex].terminal) {
      setStoryVisible(false);
      switch (data[newIndex].terminal) {
        case "phase_11":
          setLogButtonVisible(true);
        case "phase_20":
        case "phase_31":
        case "phase_32":
        case "phase_33":
        case "phase_34":
        case "phase_35":
        case "phase_36":
        case "phase_40":
        case "phase_50":
        case "phase_60":
        case "phase_70":
          setNazoVisible(true);
          break;
        case "ed_norm":
          setCurrentStep(77);
          fadeOutBGM(5);
          break;
        case "ed_true":
          setCurrentStep(99);
          break;
        default:
          break;
      }
    } else if (data[newIndex].next) {
      refreshPass(data[newIndex].next ?? "");
    } else {
      setIndex(() => newIndex);
      setStoryVisible(true);
    }
    if (data[newIndex].func) {
      switch (data[newIndex].func) {
        case "clear":
          setBGClass("");
          break;
        default:
          svgFilterModeRef.current = data[newIndex].func ?? null;
          startAnimateRef.current = new Date().getTime();
          setBGClass(data[newIndex].func ?? "");
          break;
      }
    }
  };

  const toggleLog = () => {
    if (ToggleLogRef.current) {
      setLogVisible(ToggleLogRef.current.checked);
    }
    playSE("se6");
  };

  useEffect(() => {
    setCurrentText((currentText) => data[index]?.text ?? currentText);
    if (data[index]?.text) {
      let newArr = [...logElements];
      const txt: string = data[index].text ?? "";
      const sanitizedText = DOMPurify.sanitize(txt);
      newArr.push(<p key={newArr.length} dangerouslySetInnerHTML={{ __html: sanitizedText }}></p>);
      setLogElements(newArr);
    }
    if (data[index]?.image) {
      setCurrentImage(data[index].image?.src ?? "");
    }
    if (data[index]?.sound) {
      const soundSrc = data[index]?.sound?.src;
      const soundType = data[index]?.sound?.type ?? "BGM";
      if (soundSrc) {
        if (soundType === "SE") {
          stopBGM();
          playSE(soundSrc);
        } else if (soundType === "once") {
          playBGM(soundSrc, { loop: false });
        } else {
          playBGM(soundSrc, { loop: true });
        }
      } else if (data[index]?.sound?.stop) {
        fadeOutBGM(1);
      }
    }
  }, [index]);

  const refreshPass = (newPass: string) => {
    setPass(() => newPass);
    console.log(`Load new line: ${newPass}`);
  };

  useEffect(() => {

    let animationFrameId: number;
    startAnimateRef.current = new Date().getTime();

    const animate = () => {
      let newTime = new Date().getTime();
      const delta = newTime - startAnimateRef.current;
      switch (svgFilterModeRef.current) {
        case "melt":
          if (turbulenceRef.current) {
            const freqX = Math.sin(delta / 10000) + 1.1;
            const freqY = Math.cos(delta / 10000) + 1.2;
            turbulenceRef.current.setAttribute('baseFrequency', `${freqX * 0.01} ${freqY * 0.01}`);
          }
          if (displacementRef.current) {
            displacementRef.current.setAttribute('scale', `${(delta / 700) ** 1.5}`);
          }
          break;
        case "noise":
        default:
          if (turbulenceRef.current) {
            const freqX = 0;
            const freqY = Math.cos(delta / 67) + Math.cos(delta / 247) + 2;
            turbulenceRef.current.setAttribute('baseFrequency', `${freqX} ${freqY}`);
          }
          break;
      }
      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => cancelAnimationFrame(animationFrameId);
  }, []);

  useEffect(() => {
    switch (currentStep) {
      case 11:
        setPass("prol");
        break;
      case 20:
      case 31:
      case 32:
      case 33:
      case 34:
      case 35:
      case 36:
      case 40:
      case 50:
      case 60:
      case 70:
      case 80:
      case 91:
        setNazoVisible(false);
        setPass(`story${currentStep}`);
        break;
      case 71:
        setLogVisible(false);
        stopBGM();
        break;
      case 90:
        setLogVisible(false);
        setNazoVisible(true);
    }
  }, [currentStep]);

  return <>
    <section id="bg-area">
      <img key={currentImage} src={`${process.env.PUBLIC_URL}/img/still_${currentImage}.webp`} className={BGClass} style={{ objectPosition: `${currentStep == 91 ? "top" : ""}` }}></img>
    </section>
    {isStoryVisible &&
      <section id="story" onClick={handleClick}>
        <div id="story-back"></div>
        <Message currentText={currentText}></Message>
      </section>
    }
    {isNazoVisible &&
      <NazoPhase></NazoPhase>
    }

    <svg xmlns="http://www.w3.org/2000/svg" style={{ display: "none" }}>
      <filter id="BGdisplacementFilter">
        <feColorMatrix in="SourceGraphic" type="matrix" values="0 0 0 0 0 0 1 0 0 0 0 0 0 0 0 0 0 0 1 0" result="g" />
        <feColorMatrix in="SourceGraphic" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 1 0 0 0 0 0 1 0" result="b" />
        <feColorMatrix in="SourceGraphic" type="matrix" values="1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 1 0" />
        <feOffset dx="5" dy="1" />
        <feGaussianBlur stdDeviation="2" />
        <feComposite in="g" in2="" operator="lighter" />
        <feComposite in="b" in2="" operator="lighter" result="rgb-layers" />
        <feTurbulence ref={turbulenceRef} type="turbulence" baseFrequency="0 0" numOctaves="1" result="turb" />
        <feColorMatrix
          in="turb"
          type="matrix"
          values="
            1 -1 0 0 0.5
            0 1 -1 0 0.5
            -1 0 1 0 0.5
            0 0 0 1 1"
          result="centered"
        />
        <feDisplacementMap ref={displacementRef} in2="centered" in="rgb-layers"
          scale="20"
          xChannelSelector="R"
          yChannelSelector="G"
        />
      </filter>
    </svg>

    <section id="utils">
      {currentStep <= 70 && isLogButtonVisible &&
        <label id="log-book" onClick={toggleLog}>
          <input type="checkbox" ref={ToggleLogRef} style={{ display: "none" }}></input>
          <img src={`${process.env.PUBLIC_URL}/img/log.webp`}></img>
        </label>
      }
      {currentStep <= 70 &&
        <div id="sound-button" onClick={() => { setMute(toggleMute()); }}>
          <img src={`${process.env.PUBLIC_URL}/img/icon-${isMute ? "mute" : "sound"}.png`} style={{ opacity: "0.4", filter: "invert(1)" }}></img>
        </div>
      }
    </section>
    {isLogVisible && <LogModal></LogModal>}
  </>;
}

export default Story;
